import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Card, CardBody, CardTitle, Col, Input, Row, Table } from "reactstrap";
import { useTranslation } from "react-i18next";
import { downloadCsv } from "components/stats/StatBits";
import { GetBookingPeriodStatsAPI } from "network/bookings";
import { errText } from "./errText";
import StatsCoversChart, { DINNER, LUNCH } from "./statsCoversChart";
import { DEFAULT_PERIOD, PERIODS, dayTotals, daysCount, euro, isoOf, longDate, pct, periodRange, shortDay, sourceRows } from "./statsLogic";

const SETTINGS = "/bookings/settings?tab=cover"; // apre Settings sul Valore medio a coperto
const ORANGE = "#e08a00";
const GREEN = "#2e7d32";
const RED = "#c62828";

// Riquadro con etichetta, numero grande, riga sotto e barra facoltativa.
const Tile = ({ label, value, sub, color, bar, highlight, size = { xs: 6, md: 4, xl: 2 } }) => (
  <Col {...size} className="mb-3">
    <Card className="h-100 mb-0" style={highlight ? { border: `2px solid ${LUNCH}`, background: "rgba(184,147,47,.10)" } : undefined}>
      <CardBody className="py-3">
        <div className="text-muted small text-uppercase">{label}</div>
        <div style={{ fontSize: 28, fontWeight: 600, color: color || undefined, lineHeight: 1.25 }}>{value}</div>
        {bar !== undefined && (
          <div style={{ height: 6, background: "rgba(128,128,128,.2)", borderRadius: 3, margin: "4px 0 6px" }}>
            <div style={{ width: `${Math.min(100, bar.pct)}%`, height: "100%", background: bar.color, borderRadius: 3 }} />
          </div>
        )}
        {sub && <div className="small text-muted">{sub}</div>}
      </CardBody>
    </Card>
  </Col>
);

const BookingStats = () => {
  const { t, i18n } = useTranslation("bookings");
  const lang = i18n.language === "en" ? "en" : "it";
  const today = isoOf(new Date());
  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [custom, setCustom] = useState({ from: today, to: today });
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const range = useMemo(() => periodRange(period, today, custom), [period, today, custom]);

  useEffect(() => {
    let live = true;
    setErr("");
    GetBookingPeriodStatsAPI(range.from, range.to)
      .then((r) => live && setD(r))
      .catch((e) => live && setErr(errText(e) || t("stats.loadError")));
    return () => {
      live = false;
    };
  }, [range.from, range.to, t]);

  // Personalizzato: se Dal è dopo al le date si scambiano
  const setCustomDate = (k, v) => {
    if (!v) return;
    const next = { ...custom, [k]: v };
    setCustom(next.from > next.to ? { from: next.to, to: next.from } : next);
  };
  const startCustom = () => {
    setCustom({ from: range.from, to: range.to });
    setPeriod("custom");
  };

  const n = daysCount(range.from, range.to);
  const b = d?.bookings || {};
  const c = d?.covers || {};
  const rev = d?.revenue;
  const closedOut = (b.arrived || 0) + (b.no_show || 0);
  const totals = dayTotals(d?.days);
  const sources = sourceRows(d?.sources);
  const maxSource = Math.max(1, ...sources.map((s) => s.n));
  const srcLabel = (code) => t(`source.${code}`, { defaultValue: code });

  const exportCsv = () =>
    downloadCsv(
      `${t("stats.csvName")}_${range.from}_${range.to}.csv`,
      [t("stats.colDay"), t("stats.colBookings"), t("stats.colLunch"), t("stats.colDinner"), t("stats.colTotal"), t("stats.colNoShow"), t("stats.colRevenue")],
      (d?.days || []).map((r) => [r.date, r.bookings, r.lunch, r.dinner, r.total, r.no_show, r.revenue === null ? "" : String(r.revenue).replace(".", ",")])
    );

  return (
    <>
      <div className="d-flex flex-wrap align-items-baseline mb-3" style={{ columnGap: 16 }}>
        <h3 className="mb-1">{t("stats.title")}</h3>
        {d && (
          <span className="text-muted">
            {d.cover_value
              ? <>{t("stats.coverValue", { value: euro(d.cover_value, lang) })} · <Link to={SETTINGS}>{t("stats.editInSettings")}</Link></>
              : <>{t("stats.coverValueNotSet")} · <Link to={SETTINGS}>{t("stats.setInSettings")}</Link></>}
          </span>
        )}
      </div>

      <div className="d-flex flex-wrap align-items-center mb-2" style={{ gap: 6 }}>
        {PERIODS.map((p) => (
          <Button
            key={p}
            size="sm"
            className="m-0"
            color={period === p ? "primary" : "secondary"}
            outline={period !== p}
            onClick={() => (p === "custom" ? startCustom() : setPeriod(p))}
          >
            {t(`stats.periods.${p}`)}
          </Button>
        ))}
      </div>
      {period === "custom" && (
        <div className="d-flex flex-wrap align-items-center mb-2" style={{ gap: 8 }}>
          <label className="mb-0">{t("stats.from")}</label>
          <Input type="date" bsSize="sm" style={{ width: 160 }} value={custom.from} onChange={(e) => setCustomDate("from", e.target.value)} />
          <label className="mb-0">{t("stats.to")}</label>
          <Input type="date" bsSize="sm" style={{ width: 160 }} value={custom.to} onChange={(e) => setCustomDate("to", e.target.value)} />
        </div>
      )}
      <p className="small text-muted mb-3">
        {t("stats.periodLine", { from: longDate(range.from, lang), to: longDate(range.to, lang) })} · {t("stats.daysCount", { count: n })}
      </p>

      {err && <p className="text-danger">{err}</p>}
      {!d && !err && <p>{t("stats.loading")}</p>}
      {d && (
        <>
          <h5 className="mb-2">{t("stats.bookingsTitle")}</h5>
          <Row>
            <Tile label={t("stats.received")} value={b.received} sub={t("stats.receivedSub")} />
            <Tile label={t("stats.confirmed")} value={b.confirmed} sub={t("stats.confirmedSub")} />
            <Tile label={t("stats.pending")} value={b.pending} color={b.pending > 0 ? ORANGE : undefined} sub={t("stats.pendingSub")} />
            <Tile label={t("stats.arrived")} value={b.arrived} color={GREEN} sub={t("stats.onClosed", { pct: pct(b.arrived, closedOut) })} />
            <Tile
              label={t("stats.noShow")}
              value={b.no_show}
              color={b.no_show > 0 ? RED : undefined}
              sub={`${t("stats.onClosed", { pct: pct(b.no_show, closedOut) })} · ${t("stats.lostCovers", { count: c.no_show || 0 })}`}
            />
            <Tile label={t("stats.cancelled")} value={b.cancelled} sub={t("stats.onReceived", { pct: pct(b.cancelled, b.received) })} />
          </Row>

          <h5 className="mb-2 mt-2">{t("stats.coversTitle")}</h5>
          <Row>
            <Tile size={{ xs: 6, lg: 3 }} label={t("stats.coversTotal")} value={c.total} sub={t("stats.coversTotalSub")} />
            <Tile size={{ xs: 6, lg: 3 }} label={t("stats.coversLunch")} value={c.lunch} bar={{ pct: pct(c.lunch, c.total), color: LUNCH }} sub={t("stats.ofTotal", { pct: pct(c.lunch, c.total) })} />
            <Tile size={{ xs: 6, lg: 3 }} label={t("stats.coversDinner")} value={c.dinner} bar={{ pct: pct(c.dinner, c.total), color: DINNER }} sub={t("stats.ofTotal", { pct: pct(c.dinner, c.total) })} />
            <Tile
              size={{ xs: 6, lg: 3 }}
              highlight
              label={t("stats.revenue")}
              value={rev ? euro(rev.estimated, lang) : "—"}
              sub={rev
                ? t("stats.revenueSub", { covers: c.total, value: euro(d.cover_value, lang) })
                : <Link to={SETTINGS}>{t("stats.revenueMissing")}</Link>}
            />
          </Row>
          {rev && (
            <Card className="mb-3">
              <CardBody className="py-3">
                <Row>
                  <Col xs="12" sm="4" className="mb-2 mb-sm-0">
                    <div className="text-muted small">{t("stats.collected")}</div>
                    <div style={{ fontSize: 20, fontWeight: 600 }}>{euro(rev.collected, lang)}</div>
                  </Col>
                  <Col xs="12" sm="4" className="mb-2 mb-sm-0">
                    <div className="text-muted small">{t("stats.expected")}</div>
                    <div style={{ fontSize: 20, fontWeight: 600 }}>{euro(rev.expected, lang)}</div>
                  </Col>
                  <Col xs="12" sm="4">
                    <div className="text-muted small">{t("stats.lost")}</div>
                    <div style={{ fontSize: 20, fontWeight: 600, color: RED }}>{euro(rev.lost, lang)}</div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          )}

          <Row>
            <Col lg="8">
              <Card>
                <CardBody>
                  <CardTitle tag="h5">{t("stats.chartTitle")}</CardTitle>
                  <StatsCoversChart days={d.days} />
                </CardBody>
              </Card>
            </Col>
            <Col lg="4">
              <Card>
                <CardBody>
                  <CardTitle tag="h5">{t("stats.sourcesTitle")}</CardTitle>
                  {sources.length === 0 && <p className="text-muted mb-0">{t("stats.noBookings")}</p>}
                  {sources.map((s) => (
                    <div key={s.code} className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
                      <span className="small text-truncate" style={{ width: 80, flex: "none" }}>{srcLabel(s.code)}</span>
                      <div style={{ flex: 1, height: 14, background: "rgba(128,128,128,.15)", borderRadius: 3 }}>
                        <div style={{ width: `${(s.n / maxSource) * 100}%`, height: "100%", background: LUNCH, borderRadius: 3 }} />
                      </div>
                      <strong className="small text-right" style={{ width: 32 }}>{s.n}</strong>
                    </div>
                  ))}
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Card>
            <CardBody>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <CardTitle tag="h5" className="mb-0">{t("stats.detailTitle")}</CardTitle>
                <Button size="sm" color="secondary" outline className="m-0" onClick={exportCsv}>{t("stats.export")}</Button>
              </div>
              <Table size="sm" responsive className="mb-2">
                <thead>
                  <tr>
                    <th>{t("stats.colDay")}</th>
                    <th className="text-right">{t("stats.colBookings")}</th>
                    <th className="text-right">{t("stats.colLunch")}</th>
                    <th className="text-right">{t("stats.colDinner")}</th>
                    <th className="text-right">{t("stats.colTotal")}</th>
                    <th className="text-right">{t("stats.colNoShow")}</th>
                    <th className="text-right">{t("stats.colRevenue")}</th>
                  </tr>
                </thead>
                <tbody>
                  {d.days.map((r) => (
                    <tr key={r.date} style={r.closed ? { opacity: 0.6 } : undefined}>
                      <td className="text-nowrap">
                        {shortDay(r.date, lang)}
                        {r.today && <span className="badge ml-1" style={{ background: LUNCH, color: "#fff" }}>{t("stats.todayTag")}</span>}
                        {r.closed && <span className="badge badge-secondary ml-1">{t("stats.closedTag")}</span>}
                      </td>
                      <td className="text-right">{r.bookings}</td>
                      <td className="text-right">{r.lunch}</td>
                      <td className="text-right">{r.dinner}</td>
                      <td className="text-right"><strong>{r.total}</strong></td>
                      <td className="text-right" style={r.no_show ? { color: RED } : undefined}>{r.no_show}</td>
                      <td className="text-right text-nowrap">{euro(r.revenue, lang)}</td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight: 600, borderTop: "2px solid #cfc8b8" }}>
                    <td>{t("stats.totalRow")}</td>
                    <td className="text-right">{totals.bookings}</td>
                    <td className="text-right">{totals.lunch}</td>
                    <td className="text-right">{totals.dinner}</td>
                    <td className="text-right">{totals.total}</td>
                    <td className="text-right">{totals.no_show}</td>
                    <td className="text-right text-nowrap">{euro(totals.revenue, lang)}</td>
                  </tr>
                </tbody>
              </Table>
              <p className="small text-muted mb-0">{t("stats.estimateNote")}</p>
            </CardBody>
          </Card>
        </>
      )}
    </>
  );
};

export default BookingStats;
